import React, { useState } from "react";
import { Loader2, StickyNote } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  CONTACT_MARK_PRESETS,
  CONTACT_MARK_TONE_CLASS,
  markLabel,
  toggleMark,
} from "../utils/contactMarks";

/** Composer must never show serialized annotation JSON. */
function composerSeedNotes(raw?: string | null): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  if (value.startsWith("[{") || value.startsWith("{")) return "";
  return value;
}

export type QuickAnnotateValues = {
  tags: string[];
  notes: string;
};

type Preset =
  | { id: string; label: string; tone: keyof typeof CONTACT_MARK_TONE_CLASS }
  | { id: string; labelKey: string; tone: keyof typeof CONTACT_MARK_TONE_CLASS };

type QuickAnnotatePanelProps = {
  initialTags?: string[];
  initialNotes?: string;
  /** Compact for bottom sheet after scan */
  compact?: boolean;
  saveLabel?: string;
  title?: string;
  placeholder?: string;
  presets?: ReadonlyArray<Preset>;
  onSave: (values: QuickAnnotateValues) => Promise<void>;
};

export function QuickAnnotatePanel({
  initialTags = [],
  initialNotes = "",
  compact = false,
  saveLabel,
  title,
  placeholder,
  presets = CONTACT_MARK_PRESETS,
  onSave,
}: QuickAnnotatePanelProps) {
  const { t } = useTranslation();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [notes, setNotes] = useState(() => composerSeedNotes(initialNotes));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const canSave = tags.length > 0 || notes.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setSaved(false);
    try {
      await onSave({ tags, notes: notes.trim() });
      setSaved(true);
      // Clear composer so the note lives as a card above, not stuck in the field.
      setNotes("");
      setTags([]);
      setTimeout(() => setSaved(false), 1500);
    } catch {
      // Parent shows toast; keep button as «Сохранить», not fake «Сохранено».
    } finally {
      setSaving(false);
    }
  };

  const resolvedTitle =
    title ??
    (t("annotate.title") !== "annotate.title" ? t("annotate.title") : "Заметка");
  const resolvedPlaceholder =
    placeholder ??
    (t("annotate.placeholder") !== "annotate.placeholder"
      ? t("annotate.placeholder")
      : "Комментарий к контакту…");
  const resolvedSave =
    saveLabel ??
    (t("annotate.save") !== "annotate.save" ? t("annotate.save") : "Сохранить");

  return (
    <div className={compact ? "space-y-2.5" : "space-y-3"}>
      <div className="flex items-center gap-1.5 text-[12px] text-gray-600 font-medium">
        <StickyNote size={13} className="text-indigo-500" />
        {resolvedTitle}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((preset) => {
          const on = tags.includes(preset.id);
          const tone = CONTACT_MARK_TONE_CLASS[preset.tone];
          const label =
            "label" in preset && preset.label
              ? preset.label
              : markLabel(preset.id, t);
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => setTags((prev) => toggleMark(prev, preset.id))}
              className={`px-2.5 py-1 rounded-full border text-[12px] transition-colors ${
                on ? tone.on : tone.off
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={resolvedPlaceholder}
        rows={compact ? 2 : 3}
        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
      />
      <button
        type="button"
        disabled={!canSave || saving}
        onClick={() => void handleSave()}
        className="w-full h-10 rounded-xl bg-indigo-600 text-white text-[14px] font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : null}
        {saved
          ? t("annotate.saved") !== "annotate.saved"
            ? t("annotate.saved")
            : "Сохранено"
          : resolvedSave}
      </button>
    </div>
  );
}
